import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ShoppingCart, BadgeDollarSign, Wrench, TrendingUp, Car, Layers, ChevronDown } from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell,
} from "recharts";
import api, { fmtIDR, fmtCompact, fmtDate } from "@/lib/api";
import { PAYMENT_TYPES } from "@/lib/labels";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, StatusBadge, Money, PeriodFilter, periodRange, defaultPeriod } from "@/components/common";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const STATUS_COLORS = { dalam_persiapan: "#f59e0b", siap_jual: "#10b981", booking: "#38bdf8", terjual: "#3b82f6" };
const STATUS_LABELS = { dalam_persiapan: "Dalam Persiapan", siap_jual: "Siap Jual", booking: "Booking", terjual: "Terjual" };

function StatCard({ icon: Icon, label, value, sub, accent, delay }) {
  return (
    <div
      data-testid={`stat-${label.toLowerCase().replace(/\s+/g, "-")}`}
      className="fade-up bg-[#0A0A0C] border border-[#1A1A22] rounded-md p-4 hover:-translate-y-[2px] transition-transform duration-200"
      style={{ animationDelay: `${delay}ms` }}
    >
      <div className="flex items-center justify-between mb-3">
        <p className="text-[11px] font-semibold uppercase tracking-[0.15em] text-zinc-500">{label}</p>
        <Icon className={`w-4 h-4 ${accent}`} />
      </div>
      <p className="font-heading text-xl sm:text-2xl font-extrabold tracking-tight text-zinc-50 font-mono tabular-nums">{value}</p>
      {sub && <p className="text-xs text-zinc-500 mt-1">{sub}</p>}
    </div>
  );
}

const tooltipStyle = {
  contentStyle: { backgroundColor: "#18181b", border: "1px solid #27272a", borderRadius: 6, fontSize: 12 },
  labelStyle: { color: "#fafafa", fontWeight: 600 },
  itemStyle: { color: "#a1a1aa" },
};
const BAR_RADIUS = [2, 2, 0, 0];
const TOOLTIP_CURSOR = { fill: "#27272a", opacity: 0.4 };
const fmtTooltip = (v) => fmtIDR(v);

export default function Dashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [branches, setBranches] = useState([]);
  const [branch, setBranch] = useState("all");
  const [period, setPeriod] = useState(defaultPeriod());
  const [expandedBranch, setExpandedBranch] = useState(null);

  useEffect(() => {
    api.get("/branches").then((r) => setBranches(r.data));
  }, []);

  useEffect(() => {
    setData(null);
    const params = new URLSearchParams();
    if (branch !== "all") params.set("branch_id", branch);
    const { start, end } = periodRange(period);
    if (start) {
      params.set("start", start);
      params.set("end", end);
    }
    api.get(`/dashboard/summary?${params}`).then((r) => setData(r.data));
  }, [branch, period]);

  if (!data) {
    return (
      <div className="space-y-4">
        <div className="h-8 w-48 bg-[#0A0A0C] rounded animate-pulse" />
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => <div key={i} className="h-28 bg-[#0A0A0C] border border-[#1A1A22] rounded-md animate-pulse" />)}
        </div>
      </div>
    );
  }

  const t = data.totals;
  const pieData = Object.entries(data.counts)
    .filter(([, v]) => v > 0)
    .map(([k, v]) => ({ name: STATUS_LABELS[k], value: v, color: STATUS_COLORS[k] }));
  const barData = data.monthly.map((m) => ({
    ...m,
    label: new Date(`${m.month}-01T00:00:00`).toLocaleDateString("id-ID", { month: "short" }),
  }));

  return (
    <div data-testid="dashboard-page">
      <PageHeader title="Dashboard" subtitle="Ringkasan akuntansi & performa showroom">
        <PeriodFilter value={period} onChange={setPeriod} testidPrefix="dashboard-period" />
        {user?.role !== "admin_cabang" && (
          <Select value={branch} onValueChange={setBranch}>
            <SelectTrigger data-testid="dashboard-branch-filter" className="w-44 h-10 bg-[#0F0F12] border-[#1A1A22]">
              <SelectValue placeholder="Semua Cabang" />
            </SelectTrigger>
            <SelectContent className="bg-[#0A0A0C] border-[#1A1A22]">
              <SelectItem value="all">Semua Cabang</SelectItem>
              {branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
      </PageHeader>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
        <StatCard icon={ShoppingCart} label="Total Pembelian" value={fmtIDR(t.pembelian)} accent="text-blue-500" delay={0} />
        <StatCard icon={BadgeDollarSign} label="Total Penjualan" value={fmtIDR(t.penjualan)} accent="text-emerald-500" delay={60} />
        <StatCard icon={Wrench} label="Total Biaya" value={fmtIDR(t.biaya_mobil + t.operasional + t.komisi)} sub={`Operasional ${fmtCompact(t.operasional)}`} accent="text-rose-500" delay={120} />
        <StatCard icon={TrendingUp} label="Laba Bersih" value={fmtIDR(t.laba_bersih)} sub={`Laba kotor ${fmtCompact(t.laba_kotor)}`} accent="text-blue-500" delay={180} />
        <StatCard icon={Layers} label="Nilai Modal Stok" value={fmtIDR(t.nilai_stok)} sub="Modal unit belum terjual" accent="text-blue-500" delay={240} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="md:col-span-2 fade-up bg-[#0A0A0C] border border-[#1A1A22] rounded-md p-4" style={{ animationDelay: "240ms" }}>
          <p className="text-[11px] font-semibold uppercase tracking-[0.15em] text-zinc-500 mb-4">Penjualan vs Biaya — 6 Bulan Terakhir</p>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={barData} barGap={4}>
                <CartesianGrid strokeDasharray="3 3" stroke="#27272a" vertical={false} />
                <XAxis dataKey="label" stroke="#52525b" fontSize={12} tickLine={false} axisLine={false} />
                <YAxis stroke="#52525b" fontSize={11} tickLine={false} axisLine={false} tickFormatter={fmtCompact} width={70} />
                <Tooltip {...tooltipStyle} formatter={fmtTooltip} cursor={TOOLTIP_CURSOR} />
                <Bar dataKey="penjualan" name="Penjualan" fill="#0077C8" radius={BAR_RADIUS} />
                <Bar dataKey="biaya" name="Biaya" fill="#f43f5e" radius={BAR_RADIUS} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="fade-up bg-[#0A0A0C] border border-[#1A1A22] rounded-md p-4" style={{ animationDelay: "300ms" }}>
          <p className="text-[11px] font-semibold uppercase tracking-[0.15em] text-zinc-500 mb-4">Status Stok</p>
          <div className="h-48">
            {pieData.length ? (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={pieData} dataKey="value" innerRadius={50} outerRadius={75} paddingAngle={3} strokeWidth={0}>
                    {pieData.map((d) => <Cell key={d.name} fill={d.color} />)}
                  </Pie>
                  <Tooltip {...tooltipStyle} />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-full flex flex-col items-center justify-center text-zinc-600">
                <Car className="w-8 h-8 mb-2" />
                <p className="text-sm">Belum ada mobil</p>
              </div>
            )}
          </div>
          <div className="flex justify-center gap-4 mt-2">
            {Object.entries(STATUS_LABELS).map(([k, label]) => (
              <div key={k} className="flex items-center gap-1.5 text-xs text-zinc-400">
                <span className="w-2 h-2 rounded-full" style={{ backgroundColor: STATUS_COLORS[k] }} />
                {label} ({data.counts[k] || 0})
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="fade-up bg-[#0A0A0C] border border-[#1A1A22] rounded-md mb-6" style={{ animationDelay: "340ms" }} data-testid="sales-per-branch">
        <div className="flex items-center justify-between p-4 border-b border-[#1A1A22]">
          <p className="text-[11px] font-semibold uppercase tracking-[0.15em] text-zinc-500">Total Penjualan per Cabang</p>
          <Link to="/penjualan" data-testid="view-sales-per-branch-link" className="text-xs text-blue-500 hover:text-blue-400 transition-colors duration-200">Detail penjualan</Link>
        </div>
        {(data.penjualan_per_cabang || []).length === 0 ? (
          <p className="p-6 text-sm text-zinc-500">Belum ada penjualan pada periode ini.</p>
        ) : (
          <table className="w-full text-sm">
            <tbody>
              {data.penjualan_per_cabang.map((b) => {
                const key = b.branch_id || "umum";
                const open = expandedBranch === key;
                return [
                  <tr key={key} data-testid={`branch-row-${key}`} onClick={() => setExpandedBranch(open ? null : key)} className="cursor-pointer border-b border-[#1A1A22]/60 hover:bg-[#121218]/50 transition-colors duration-200">
                    <td className="px-4 py-3 font-medium text-zinc-100">
                      <span className="inline-flex items-center gap-2">
                        <ChevronDown className={`w-4 h-4 text-zinc-500 transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
                        {b.branch_name}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-xs text-zinc-400">{b.count} unit terjual</td>
                    <td className="px-4 py-3 text-right"><Money value={b.total} className="text-emerald-500 font-semibold" /></td>
                  </tr>,
                  ...(open ? (b.items || []).map((it, ii) => (
                    <tr key={`${key}-${ii}`} className="border-b border-[#1A1A22]/40 bg-[#050505]/40">
                      <td className="px-4 py-2.5 pl-10">
                        <p className="text-zinc-200">{it.car_name}</p>
                        <p className="text-xs text-zinc-500">{it.buyer_name} · {fmtDate(it.date)}</p>
                      </td>
                      <td className="px-4 py-2.5 text-xs text-zinc-500">{PAYMENT_TYPES[it.payment_type] || it.payment_type}</td>
                      <td className="px-4 py-2.5 text-right"><Money value={it.price} className="text-zinc-100" /></td>
                    </tr>
                  )) : []),
                ];
              })}
              <tr className="bg-[#050505]/50 border-t border-[#1A1A22]">
                <td colSpan={2} className="px-4 py-3 text-xs font-semibold uppercase tracking-[0.15em] text-zinc-500">
                  Total · {(data.penjualan_per_cabang || []).reduce((a, b) => a + b.count, 0)} unit
                </td>
                <td className="px-4 py-3 text-right"><Money value={t.penjualan} className="text-emerald-500 font-semibold" /></td>
              </tr>
            </tbody>
          </table>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="fade-up bg-[#0A0A0C] border border-[#1A1A22] rounded-md" style={{ animationDelay: "360ms" }}>
          <div className="flex items-center justify-between p-4 border-b border-[#1A1A22]">
            <p className="text-[11px] font-semibold uppercase tracking-[0.15em] text-zinc-500">Penjualan Terbaru</p>
            <Link to="/penjualan" data-testid="view-all-sales-link" className="text-xs text-blue-500 hover:text-blue-400 transition-colors duration-200">Lihat semua</Link>
          </div>
          {data.recent_sales.length === 0 ? (
            <p className="p-6 text-sm text-zinc-500">Belum ada penjualan.</p>
          ) : (
            <table className="w-full text-sm">
              <tbody>
                {data.recent_sales.map((s) => (
                  <tr key={s.id} className="border-b border-[#1A1A22]/60 last:border-0 hover:bg-[#121218]/50 transition-colors duration-200">
                    <td className="px-4 py-3">
                      <p className="font-medium text-zinc-100">{s.car_name}</p>
                      <p className="text-xs text-zinc-500">{s.buyer_name} · {fmtDate(s.date)}</p>
                    </td>
                    <td className="px-4 py-3 text-right"><Money value={s.price} className="text-zinc-100" /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="fade-up bg-[#0A0A0C] border border-[#1A1A22] rounded-md" style={{ animationDelay: "420ms" }}>
          <div className="flex items-center justify-between p-4 border-b border-[#1A1A22]">
            <p className="text-[11px] font-semibold uppercase tracking-[0.15em] text-zinc-500">Laba Tertinggi per Mobil</p>
            <Link to="/laporan" data-testid="view-reports-link" className="text-xs text-blue-500 hover:text-blue-400 transition-colors duration-200">Laporan</Link>
          </div>
          {data.top_profit.length === 0 ? (
            <p className="p-6 text-sm text-zinc-500">Belum ada mobil terjual.</p>
          ) : (
            <table className="w-full text-sm">
              <tbody>
                {data.top_profit.map((p, i) => (
                  <tr key={i} className="border-b border-[#1A1A22]/60 last:border-0 hover:bg-[#121218]/50 transition-colors duration-200">
                    <td className="px-4 py-3">
                      <p className="font-medium text-zinc-100">{p.car_name}</p>
                      <p className="text-xs text-zinc-500">Jual {fmtCompact(p.harga_jual)} · HPP {fmtCompact(p.hpp)}</p>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <Money value={p.laba} className={p.laba >= 0 ? "text-emerald-500" : "text-rose-500"} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <div className="mt-6 flex flex-wrap gap-2">
        <StatusBadge status="dalam_persiapan" />
        <StatusBadge status="siap_jual" />
        <StatusBadge status="terjual" />
      </div>
    </div>
  );
}
